import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Download, Copy } from 'lucide-react';

const ChatTable = ({ tableData, isExpanded = false }) => {
  const [expanded, setExpanded] = useState(isExpanded);
  const [copiedMessage, setCopiedMessage] = useState('');

  if (!tableData) return null;

  const { headers, rows, title, summary } = tableData;

  const handleCopyTable = () => {
    const tableText = [
      title || 'Table Data',
      headers.join('\t'),
      ...rows.map(row => row.join('\t')),
      summary ? `\nSummary: ${summary}` : ''
    ].join('\n');
    
    navigator.clipboard.writeText(tableText);
    setCopiedMessage('Table copied to clipboard!');
    setTimeout(() => setCopiedMessage(''), 2000);
  };

  const exportToCSV = () => {
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = `${title || 'table_data'}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const maxVisibleRows = 5;
  const displayRows = expanded ? rows : rows.slice(0, maxVisibleRows);
  const hasMoreRows = rows.length > maxVisibleRows;

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg my-3 max-w-full overflow-hidden">
      {/* Table Header */}
      <div className="flex items-center justify-between p-3 bg-gray-700">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-medium text-white">
            {title || 'Data Table'}
          </h4>
          <span className="px-2 py-1 bg-blue-600 text-blue-100 text-xs rounded">
            {rows.length} {rows.length === 1 ? 'row' : 'rows'}
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          {copiedMessage && (
            <span className="text-xs text-green-400">{copiedMessage}</span>
          )}
          
          <button
            onClick={handleCopyTable}
            className="p-1 text-gray-400 hover:text-white transition-colors"
            title="Copy table"
          >
            <Copy className="w-4 h-4" />
          </button>
          
          <button
            onClick={exportToCSV}
            className="p-1 text-gray-400 hover:text-white transition-colors"
            title="Export to CSV"
          >
            <Download className="w-4 h-4" />
          </button>
          
          {hasMoreRows && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="p-1 text-gray-400 hover:text-white transition-colors"
              title={expanded ? "Show less" : "Show all"}
            >
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          )}
        </div>
      </div>

      {/* Table Content */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-750">
            <tr>
              {headers.map((header, index) => (
                <th key={index} className="px-3 py-2 text-left text-gray-300 font-medium border-b border-gray-600">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayRows.map((row, rowIndex) => (
              <tr key={rowIndex} className={`border-b border-gray-700 ${rowIndex % 2 === 0 ? 'bg-gray-800' : 'bg-gray-750'}`}>
                {row.map((cell, cellIndex) => (
                  <td key={cellIndex} className="px-3 py-2 text-gray-200">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Show More Indicator */}
      {hasMoreRows && !expanded && (
        <div className="p-2 bg-gray-750 text-center">
          <button
            onClick={() => setExpanded(true)}
            className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
          >
            Show {rows.length - maxVisibleRows} more rows...
          </button>
        </div>
      )}

      {/* Summary */}
      {summary && (
        <div className="p-3 bg-gray-750 border-t border-gray-700">
          <p className="text-xs text-gray-400">{summary}</p>
        </div>
      )}
    </div>
  );
};

export default ChatTable;